import type { DeckSource } from '../../sync-engine/schema/shared-types';
import {
  generationSourceEntries,
  type GenerationSourceEntry,
  generationSourceKey,
} from './generate-sources';
import type { DeckMaterialItem } from './types';

/**
 * What an ASSESSMENT — a quiz, an oral exam, a case study — can be built
 * from, as its source dropdown lists it: every document of the deck by name,
 * one row each, then the deck's flashcards with their count ("122
 * flashcards"). The rows, their names and their order come from the SAME
 * shared builder the Generate menu and the All Sources grid use, so a
 * document cannot be missing here and present there, or wear two names.
 *
 * TITLE ONLY. A row is a document's name; a sentence under every one said
 * the same thing six times. The one line a row may carry is the REASON it
 * cannot be picked: a document still uploading ("Uploading…"), or one with
 * no readable text — listed, disabled, and said, because a document in the
 * deck's grid and missing from this menu reads as a bug.
 *
 * ONE at a time: an assessment reads one body of text, the way a generated
 * batch cites one document. Given the deck's notes among `items`, every note is
 * a row of its own, named as the materials grid names it; without them, one
 * Notes row reads the note `note-usability` chooses.
 */

/** The row for the deck's own flashcards. */
export const FLASHCARDS_PICK_KEY = generationSourceKey({ kind: 'flashcards' });

/** A document's row, by its source id. */
export const sourcePickKey = (sourceId: string): string =>
  generationSourceKey({ kind: 'source', sourceId });

/** The source id a document row stands for, or `null` for any other row. */
export function sourceIdOfPickKey(key: string): string | null {
  return key.startsWith('source:') ? key.slice('source:'.length) : null;
}

export const notePickKey = (noteId: string): string =>
  generationSourceKey({ kind: 'note', noteId });

/** The note a note row reads, or `null` for any other row. */
export function noteIdOfPickKey(key: string): string | null {
  return key.startsWith('note:') ? key.slice('note:'.length) : null;
}

export interface SourcePickCopy {
  /** "122 flashcards" — the flashcards row, count included. */
  flashcards: string;
  /** "Uploading…" — under a document still on its way in. */
  uploading: string;
  /** Under a document with no readable text. */
  unreadable: string;
  notes?: string;
  noNotes?: string;
  /** "Reading your notes…" — under a note while it is still being read. */
  notesLoading?: string;
  /** Under a note too short to assess (`isTooShortToAssess`). */
  notesTooShort?: string;
}

type NotesState = { noteId: string | undefined; loading?: boolean; tooShort?: boolean };

/**
 * The rows, from the deck's source items. Pure — the hook below binds the
 * copy. `sources` is read for one thing: whether a document has a file an
 * assessment can take paragraphs from; one without (pasted text, a source
 * that never processed) is offered disabled with the reason.
 */
export function sourcePickEntries({
  items,
  sources,
  flashcardsCount,
  copy,
  notes,
}: {
  items: readonly DeckMaterialItem[];
  sources: readonly DeckSource[];
  flashcardsCount: number;
  copy: SourcePickCopy;
  /**
   * The note being read: the pick, or the default one. `noteId` undefined = no
   * note has writing in it — or, while `loading`, none has been read yet. A tier
   * still arriving reads as loading, never absent. `tooShort`: the note has too
   * little text — offered disabled, like an absent one, and said; loading still
   * wins. Only the note being read carries a state: the others are offered
   * unread, and read when picked.
   */
  notes?: NotesState;
}): GenerationSourceEntry[] {
  const fileBySourceId = new Map(
    sources.map((source) => [source.sourceId, source.pdfInfo?.filePath] as const),
  );
  const documents = items.filter((item) => item.ref.type === 'source');
  const noteItems = items.filter((item) => item.ref.type === 'note');
  const unreadable = new Set(
    documents
      .filter(
        (item) =>
          item.ref.type === 'source' &&
          !item.pending &&
          !fileBySourceId.get(item.ref.sourceId),
      )
      .map((item) => item.key),
  );
  const entries = generationSourceEntries({
    items: documents,
    hasCards: flashcardsCount > 0,
    flashcardsLabel: copy.flashcards,
    pendingHint: copy.uploading,
    unavailableHint: copy.unreadable,
    emptyKeys: unreadable,
  });
  if (!notes) return entries;
  entries.splice(
    entries.length - 1,
    0,
    ...(noteItems.length ? noteRows(noteItems, notes, copy) : [notesRow(notes, copy)]),
  );
  return entries;
}

/** One row per note. With none read yet, or none with writing, every row is off. */
function noteRows(
  noteItems: readonly DeckMaterialItem[],
  notes: NotesState,
  copy: SourcePickCopy,
): GenerationSourceEntry[] {
  return noteItems.map((item) => {
    const noteId = item.ref.type === 'note' ? item.ref.noteId : '';
    const read = notes.noteId === undefined || notes.noteId === noteId;
    const off = read && (!notes.noteId || !!notes.loading || !!notes.tooShort);
    return {
      pick: { kind: 'note', noteId },
      key: notePickKey(noteId),
      label: item.label,
      kind: 'note',
      disabled: off,
      hint: !off ? undefined : notes.loading ? copy.notesLoading : copy.notesTooShort,
    };
  });
}

/** The one Notes row, reading the note `note-usability` chooses. */
function notesRow(notes: NotesState, copy: SourcePickCopy): GenerationSourceEntry {
  const noteId = notes.noteId ?? '';
  const off = !notes.noteId || !!notes.tooShort;
  return {
    pick: { kind: 'note', noteId },
    key: notePickKey(noteId),
    label: copy.notes ?? '',
    kind: 'note',
    disabled: off,
    hint: !off
      ? undefined
      : notes.loading
        ? copy.notesLoading
        : notes.tooShort
          ? copy.notesTooShort
          : copy.noNotes,
  };
}
